import {
    ConversationsAction,
    ConversationStateStatus,
} from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import { Dispatch } from "react";
import { ResultRecord, StringUtils } from "@rsm-hcd/javascript-core";
import ConversationMessageFeedbackRecord from "models/view-models/conversations/conversation-message-feedback-record";
import MessageFeedbackService, {
    MessageFeedbackBasePathParams,
} from "utilities/services/conversations/message-feedback-service";

export const createMessageRating =
    (feedback: ConversationMessageFeedbackRecord) =>
    async (dispatch: Dispatch<ConversationsAction>) => {
        let result: ResultRecord<boolean> | undefined;

        dispatch({
            type: "LOADING_MESSAGE_FEEDBACK",
            status: ConversationStateStatus.MessageFeedbackLoading,
            messageId: feedback.messageId!,
        });

        try {
            // Validate that this is actually a new feedback (no ID)
            if (feedback.feedbackId) {
                dispatch({
                    type: "ERROR",
                    error: new Error(
                        "Cannot create feedback: Feedback already has an ID. Use updateMessageRating for existing feedback."
                    ),
                });
                return;
            }

            if (
                StringUtils.isEmpty(feedback.messageId) ||
                StringUtils.isEmpty(feedback.conversationId)
            ) {
                dispatch({
                    type: "ERROR",
                    error: new Error(
                        "Message ID and Conversation ID are required to create a message."
                    ),
                });
                return;
            }

            const createMessageFeedbackPathParams: MessageFeedbackBasePathParams =
                {
                    conversationId: feedback.conversationId,
                    messageId: feedback.messageId,
                };

            // For creation, only send the rating initially - category and comment are optional
            const createFeedbackData = new ConversationMessageFeedbackRecord({
                rating: feedback.rating,
                ...(feedback.feedbackCategory !== undefined && {
                    feedbackCategory: feedback.feedbackCategory,
                }),
                ...(feedback.feedbackComment !== undefined && {
                    feedbackComment: feedback.feedbackComment,
                }),
                conversationId: feedback.conversationId,
                messageId: feedback.messageId,
            });

            const feedbackResponse =
                await MessageFeedbackService.createFeedbackRating(
                    createFeedbackData,
                    createMessageFeedbackPathParams
                );

            // Defensive programming: Ensure we have a valid response structure
            if (!feedbackResponse || !feedbackResponse.resultObject) {
                dispatch({
                    type: "ERROR",
                    error: new Error("Invalid response from feedback API"),
                });
                return;
            }

            // Update state with the feedback ID returned from the API
            const feedbackWithId = feedback.with({
                feedbackId: feedbackResponse.resultObject.feedbackId,
            });

            dispatch({
                type: "UPDATE_MESSAGE_FEEDBACK",
                feedback: feedbackWithId,
            });
            return;
        } catch (error) {
            result = new ResultRecord<boolean>().addError("ERROR", `${error}`);

            dispatch({
                type: "ERROR",
                error: new Error(
                    (
                        result?.listErrorMessages() || [
                            "Failed to update conversation",
                        ]
                    ).join(", ")
                ),
            });

            return;
        }
    };
